import React from 'react';
import { FieldArray, useFormikContext } from 'formik';

import Button from '~/components/core/Atomic/Buttons/Button';
import MenuItem from '~/components/core/Atomic/MenuItem';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import { TrashIcon } from '~/components/icons';
import InlineIconButtonUntyped from '~/components/InlineIconButton';
import TextFieldFormik from '~/components/TextFieldFormik';

import TEXTS from './texts';
import type { ConfigFormValues } from './types';
import { emptyQueryParamRow, QUERY_PARAM_SOURCE_LABELS } from './types';

// InlineIconButton is a .jsx file; under allowJs its optional props (including `ariaLabel`) are
// inferred as the literal `undefined` type. Cast to the runtime shape so a real label can be passed.
interface InlineIconButtonProps {
  icon: React.ElementType;
  tooltipTitle?: string;
  ariaLabel?: string;
  onClick?: () => void;
  disabled?: boolean;
}
const InlineIconButton = InlineIconButtonUntyped as React.ComponentType<InlineIconButtonProps>;

// The params the customer's endpoint expects. The operator names each one (it is their endpoint's
// contract) and picks WHICH FiveSigma value fills it — never the value itself, which is resolved per
// call from the claim Clive is working on. `sources` is what the selected tool actually carries, so
// the dropdown can only offer bindings the backend will accept.
const QueryParamsEditor: React.FC<{ sources: string[] }> = ({ sources }) => {
  const { values } = useFormikContext<ConfigFormValues>();

  return (
    <div className="flex flex-col gap-8">
      <Text variant={Text.VARIANTS.XS}>{TEXTS.queryParams.helper}</Text>
      <FieldArray
        name="query_params"
        render={({ push, remove }) => (
          <div className="flex flex-col gap-8">
            {values.query_params.map((_, idx) => (
              <div className="flex items-center gap-8" key={idx}>
                <TextFieldFormik
                  id={`query_params.${idx}.name`}
                  name={`query_params.${idx}.name`}
                  label={TEXTS.queryParams.paramName}
                  fullWidth
                />
                <TextFieldFormik
                  select
                  id={`query_params.${idx}.value`}
                  name={`query_params.${idx}.value`}
                  label={TEXTS.queryParams.paramValue}
                  fullWidth
                >
                  {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                  {/* @ts-ignore TextFieldFormik (JS) is not typed for select children */}
                  {sources.map((source) => (
                    <MenuItem key={source} value={source}>
                      {QUERY_PARAM_SOURCE_LABELS[source] ?? source}
                    </MenuItem>
                  ))}
                </TextFieldFormik>
                <InlineIconButton
                  icon={TrashIcon}
                  tooltipTitle={TEXTS.queryParams.removeParam}
                  ariaLabel={TEXTS.queryParams.removeParam}
                  onClick={() => remove(idx)}
                />
              </div>
            ))}
            <Button
              className="self-start"
              variant="text"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => push(emptyQueryParamRow())}
            >
              {TEXTS.queryParams.addParam}
            </Button>
          </div>
        )}
      />
    </div>
  );
};

export default QueryParamsEditor;
